import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDialog } from '../contexts/DialogContext';
import clienteService from '../services/clienteService';
import LoadingSpinner from '../components/LoadingSpinner';
import './Clientes.css';

const Clientes = () => {
  const { user } = useAuth();
  const { showConfirm } = useDialog();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [paginaActual, setPaginaActual] = useState(1);
  const [itemsPorPagina, setItemsPorPagina] = useState(10);

  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    email: '',
    direccion: '',
    observaciones: '',
    sucursal: '' // Para cuando el admin crea cliente
  });

  // Cargar clientes desde la API
  const cargarClientes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clienteService.getClientes();
      console.log('Respuesta clientes:', response);
      
      // Si es admin, mostrar todos los clientes
      // Si no, filtrar solo clientes de su sucursal
      const clientesFiltrados = user?.rol === 'admin' 
        ? (response.data || [])
        : (response.data || []).filter(cliente => cliente.sucursal === user?.sucursal);
      
      setClientes(clientesFiltrados);
      setPaginaActual(1);
    } catch (error) {
      console.error('Error cargando clientes:', error);
      setError('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    setFormErrors((prev) => {
      if (!prev[name] && !prev._general) return prev;
      return {
        ...prev,
        [name]: undefined,
        _general: undefined
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    
    try {
      if (editingCliente) {
        // Actualizar cliente
        await clienteService.updateCliente(editingCliente._id, formData);
      } else {
        // Crear nuevo cliente
        // Si es admin, usar la sucursal seleccionada, sino la sucursal del usuario
        const sucursalFinal = user?.rol === 'admin' ? formData.sucursal : user?.sucursal;
        
        if (!sucursalFinal) {
          setFormErrors({ sucursal: 'Debe seleccionar una sucursal' });
          return;
        }
        
        await clienteService.createCliente({
          ...formData,
          sucursal: sucursalFinal
        });
      }
      
      // Recargar la lista de clientes
      await cargarClientes();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Error guardando cliente:', error);
      const data = error?.response?.data || error;
      if (data?.fields) {
        setFormErrors({ ...data.fields, _general: data.message });
      } else {
        setFormErrors({ _general: data?.message || 'Error al guardar el cliente' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      telefono: '',
      email: '',
      direccion: '',
      observaciones: '',
      sucursal: ''
    });
    setEditingCliente(null);
    setFormErrors({});
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setFormData({
      nombre: cliente.nombre,
      telefono: cliente.telefono,
      email: cliente.email,
      direccion: cliente.direccion,
      observaciones: cliente.observaciones,
      sucursal: cliente.sucursal
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmado = await showConfirm('¿Estás seguro de eliminar este cliente? Esta acción no se puede deshacer.', {
      title: 'Eliminar cliente',
      confirmLabel: 'Eliminar',
    });
    if (!confirmado) return;
    try {
      await clienteService.deleteCliente(id);
      await cargarClientes();
    } catch (error) {
      console.error('Error eliminando cliente:', error);
      setError('Error al eliminar el cliente');
    }
  };

  const clientesFiltrados = clientes.filter(cliente =>
    cliente.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    (cliente.email || '').toLowerCase().includes(busqueda.toLowerCase()) ||
    (cliente.telefono || '').includes(busqueda)
  );

  const totalItems = clientesFiltrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalItems / itemsPorPagina));
  const indiceInicio = (paginaActual - 1) * itemsPorPagina;
  const indiceFin = indiceInicio + itemsPorPagina;
  const clientesPaginados = clientesFiltrados.slice(indiceInicio, indiceFin);

  const irAPagina = (pagina) => {
    if (pagina >= 1 && pagina <= totalPaginas) {
      setPaginaActual(pagina);
    }
  };

  useEffect(() => {
    setPaginaActual(1);
  }, [busqueda]);

  return (
    <div className="clientes-container">
      {loading && (
        <LoadingSpinner label="Cargando clientes..." />
      )}
      
      {!loading && error && (
        <div className="error-message" style={{
          background: '#ffebee',
          color: '#c62828',
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          border: '1px solid #ffcdd2'
        }}>
          {error}
          <button 
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              float: 'right',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>
      )}
      
      {!loading && (
        <>
          <div className="clientes-header">
            <h1>Gestión de Clientes</h1>
            <button 
              className="btn btn-primary"
              onClick={() => setShowModal(true)}
            >
              + Nuevo Cliente
            </button>
          </div>

          {/* Barra de búsqueda */}
          <div className="search-bar">
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Lista de clientes */}
          <div className="clientes-grid">
            {clientesPaginados.map(cliente => (
          <div key={cliente._id} className="cliente-card">
            <div className="cliente-info">
              <h3>
                {cliente.nombre}
                {user?.rol === 'admin' && (
                  <span className="sucursal-badge">
                    {cliente.sucursal === 'parana' && '🟢 Paraná'}
                    {cliente.sucursal === 'rosario' && '🔵 Rosario'}
                    {cliente.sucursal === 'esperanza' && '🟡 Esperanza'}
                  </span>
                )}
              </h3>
              <p><strong>📞</strong> {cliente.telefono}</p>
              <p><strong>📧</strong> {cliente.email}</p>
              <p><strong>📍</strong> {cliente.direccion}</p>
              {cliente.observaciones && (
                <p className="observaciones">
                  <strong>💭</strong> {cliente.observaciones}
                </p>
              )}
            </div>
            
            <div className="cliente-actions">
              <button 
                className="btn btn-edit"
                onClick={() => handleEdit(cliente)}
              >
                ✏️ Editar
              </button>
              <button 
                className="btn btn-delete"
                onClick={() => handleDelete(cliente._id)}
              >
                🗑️ Eliminar
              </button>
            </div>
          </div>
        ))}
          </div>

          {clientesFiltrados.length === 0 && (
            <div className="no-results">
              {busqueda ? 'No se encontraron clientes con esa búsqueda' : 'No hay clientes registrados'}
            </div>
          )}

          {clientesFiltrados.length > 0 && (
            <div className="paginacion-container">
              <div className="paginacion-info">
                Mostrando {indiceInicio + 1}-{Math.min(indiceFin, totalItems)} de {totalItems} clientes
              </div>

              <div className="paginacion-controles">
                <button onClick={() => irAPagina(1)} disabled={paginaActual === 1} className="btn-paginacion">«</button>
                <button onClick={() => irAPagina(paginaActual - 1)} disabled={paginaActual === 1} className="btn-paginacion">‹</button>

                <div className="paginacion-paginas">
                  {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                    .filter(pagina => pagina === 1 || pagina === totalPaginas || (pagina >= paginaActual - 2 && pagina <= paginaActual + 2))
                    .map((pagina, index, array) => (
                      <React.Fragment key={pagina}>
                        {index > 0 && array[index - 1] !== pagina - 1 && <span className="paginacion-ellipsis">...</span>}
                        <button onClick={() => irAPagina(pagina)} className={`btn-paginacion ${paginaActual === pagina ? 'active' : ''}`}>
                          {pagina}
                        </button>
                      </React.Fragment>
                    ))}
                </div>

                <button onClick={() => irAPagina(paginaActual + 1)} disabled={paginaActual === totalPaginas} className="btn-paginacion">›</button>
                <button onClick={() => irAPagina(totalPaginas)} disabled={paginaActual === totalPaginas} className="btn-paginacion">»</button>
              </div>

              <div className="paginacion-selector">
                <label>Por página:</label>
                <select
                  value={itemsPorPagina}
                  onChange={(e) => {
                    setItemsPorPagina(Number(e.target.value));
                    setPaginaActual(1);
                  }}
                  className="select-items-pagina"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal para crear/editar cliente */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="cliente-form">
              {formErrors._general && (
                <div className="form-error-global">{formErrors._general}</div>
              )}

              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="text"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className={formErrors.telefono ? 'input-error' : ''}
                />
                {formErrors.telefono && <small className="field-error">{formErrors.telefono}</small>}
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={formErrors.email ? 'input-error' : ''}
                />
                {formErrors.email && <small className="field-error">{formErrors.email}</small>}
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              {/* Campo de sucursal solo visible para admin al crear */}
              {user?.rol === 'admin' && !editingCliente && (
                <div className="form-group">
                  <label>Sucursal *</label>
                  <select
                    name="sucursal"
                    value={formData.sucursal}
                    onChange={handleInputChange}
                    className={formErrors.sucursal ? 'input-error' : ''}
                    required
                  >
                    <option value="">Seleccione una sucursal</option>
                    <option value="parana">Paraná</option>
                    <option value="rosario">Rosario</option>
                    <option value="esperanza">Esperanza</option>
                  </select>
                  {formErrors.sucursal && <small className="field-error">{formErrors.sucursal}</small>}
                </div>
              )}

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary">
                  {editingCliente ? 'Actualizar' : 'Crear'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clientes;